import React, { useRef, useState } from "react";
import { CsvMessageRow } from "../../types";
import { sendCsvFlashMessage } from "../../api/new-api";
import Papa from "papaparse";

type Props = {
  refetch: any;
};

const CsvImportPage: React.FC<Props> = ({ refetch }: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [notificationsCount, setNotificationsCount] = useState(0);

  const requiredColumns = [
    "cmsPlayerId",
    "playerAccountNum",
    "message",
    "title",
  ];

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileUpload = (file: File) => {
    setIsLoading(true);
    setIsSuccess(false);
    setIsError(false);
    setErrorMessage("");

    Papa.parse<CsvMessageRow>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results: any) => {
        const { data, errors, meta } = results;

        // Validate column names
        const uploadedColumns = meta.fields || [];
        const missingColumns = requiredColumns.filter(
          (col) => !uploadedColumns.includes(col)
        );

        if (missingColumns.length > 0) {
          setIsLoading(false);
          setIsError(true);
          setErrorMessage(
            `Missing required columns: ${missingColumns.join(", ")}`
          );
          resetFileInput();
          return;
        }

        if (errors.length > 0) {
          setIsLoading(false);
          setIsError(true);
          setErrorMessage("Error parsing the CSV file.");
          resetFileInput();
          return;
        }

        // Set the notification count based on parsed rows
        const notificationCount = data.length;
        setNotificationsCount(notificationCount);

        // Call API
        sendCsvFlashMessage(data)
          .then(() => {
            setIsSuccess(true);
          })
          .catch((error) => {
            setIsError(true);
            setErrorMessage(error.message || "Failed to send CSV data.");
          })
          .finally(() => {
            setIsLoading(false);
            resetFileInput();
          });
      },
      error: (err: any) => {
        setIsLoading(false);
        setIsError(true);
        setErrorMessage(err.message || "Error reading the file.");
        resetFileInput();
      },
    });
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };
  const handleReset = () => {
    resetFileInput();
    setIsLoading(false);
    setIsSuccess(false);
    setIsError(false);
    setErrorMessage("");
    setNotificationsCount(0);
  };

  const handleDownloadSample = () => {
    const sampleRow: CsvMessageRow = {
      cmsPlayerId: "132",
      playerAccountNum: "132",
      message: "You are receiving a custom message!",
      title: "Hello!",
    };
    const csv = Papa.unparse({
      fields: requiredColumns,
      data: [requiredColumns.map((col) => sampleRow[col as keyof CsvMessageRow])],
    });

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "sample-flash-messages.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="flex justify-center items-center ">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-6">
        <h1 className="text-xl font-bold text-gray-800 mb-4">
          CSV Import Page
        </h1>
        <p className="text-gray-600 mb-6">
          Please upload your CSV file to proceed or download the sample file for
          reference.
        </p>

        <input
          type="file"
          accept=".csv"
          onChange={handleFileChange}
          ref={fileInputRef}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200"
        />

        <div className="flex justify-between items-center mt-6">
          <button
            type="button"
            onClick={handleDownloadSample}
            className="text-sm font-medium text-primary-fg bg-primary hover:bg-primary-hover px-4 py-2 rounded"
          >
            Download Sample CSV
          </button>
        </div>

        {isLoading && (
          <p className="mt-4 text-primary animate-pulse">Uploading...</p>
        )}
        {isSuccess && (
          <p className="mt-4 text-green-500">
            Sent {notificationsCount} notifications successfully!
          </p>
        )}
        {isError && <p className="mt-4 text-red-500">{errorMessage}</p>}
      </div>
    </div>
  );
};

export default CsvImportPage;
